/**
 * BLU-1519: the job page's stuck-variant list. Plain words per reason, the
 * suggested fix, the Shopify admin link, and the Check again outcome copy.
 *
 * Keys are written out in full (never built from the reason code) so the
 * i18n check can see every one is used.
 */
import { shopifyGidNumericId, type CoverageStuckReason, type CoverageStuckVariant } from '@threadheads/shared';
import { getShopifyAdminVariantUrl } from '@/lib/shopify-urls';

export const STUCK_REASON_KEYS: Record<CoverageStuckReason, { reason: string; action: string }> = {
  not_tracked: {
    reason: 'job.inventoryCoverage.reason.notTracked',
    action: 'job.inventoryCoverage.action.turnOnSettings',
  },
  not_physical: {
    reason: 'job.inventoryCoverage.reason.notPhysical',
    action: 'job.inventoryCoverage.action.turnOnSettings',
  },
  enable_failed: {
    reason: 'job.inventoryCoverage.reason.enableFailed',
    action: 'job.inventoryCoverage.action.turnOnSettingsThenCheck',
  },
  push_not_live: {
    reason: 'job.inventoryCoverage.reason.pushNotLive',
    action: 'job.inventoryCoverage.action.turnOnStockPush',
  },
  variant_missing: {
    reason: 'job.inventoryCoverage.reason.variantMissing',
    action: 'job.inventoryCoverage.action.syncAgain',
  },
  projection_missing: {
    reason: 'job.inventoryCoverage.reason.projectionMissing',
    action: 'job.inventoryCoverage.action.syncAgain',
  },
  base_sku_changed: {
    reason: 'job.inventoryCoverage.reason.baseSkuChanged',
    action: 'job.inventoryCoverage.action.syncAgain',
  },
  identity_mismatch: {
    reason: 'job.inventoryCoverage.reason.identityMismatch',
    action: 'job.inventoryCoverage.action.syncAgain',
  },
};

/** An `enable_failed` PrintRun was not allowed to make (store guard or missing access). */
const ENABLE_REFUSED_KEYS = {
  reason: 'job.inventoryCoverage.reason.enableRefused',
  action: 'job.inventoryCoverage.action.turnOnSettingsThenCheck',
} as const;

/** The copy for one row: its reason's keys, or the refusal wording when PrintRun may not change the store. */
export function stuckReasonKeys(
  row: Pick<CoverageStuckVariant, 'reason' | 'refusal'>,
): { reason: string; action: string } {
  return row.reason === 'enable_failed' && row.refusal ? ENABLE_REFUSED_KEYS : STUCK_REASON_KEYS[row.reason];
}

/**
 * The variant's page in Shopify admin, from the org's `*.myshopify.com`
 * domain (reuses `getShopifyAdminVariantUrl`). Falls back to the product page
 * when the variant id is unusable, and null when there is no product to open.
 */
export function stuckVariantAdminUrl(
  shopifyDomain: string | null | undefined,
  variant: Pick<CoverageStuckVariant, 'productId' | 'variantId'>,
): string | null {
  return getShopifyAdminVariantUrl(
    shopifyDomain,
    shopifyGidNumericId(variant.productId),
    shopifyGidNumericId(variant.variantId),
  );
}

export type CoverageCheckOutcome = 'started' | 'running' | 'done' | 'ended' | 'not_ready';

/** Toast copy for each Check again answer. */
export const CHECK_AGAIN_TOAST_KEYS: Record<CoverageCheckOutcome, string> = {
  started: 'job.inventoryCoverage.checkStarted',
  running: 'job.inventoryCoverage.checkRunning',
  done: 'job.inventoryCoverage.checkDone',
  ended: 'job.inventoryCoverage.checkEnded',
  not_ready: 'job.inventoryCoverage.checkNotReady',
};
